import React, { useState, useEffect } from 'react';
import { db } from '../db';
import { Plus, Target, CheckCircle, Trophy, TrendingUp, X, Trash2, Calendar, Info } from 'lucide-react';
import { toast, confirmDialog } from '../utils/feedback';
import { getEarnedBadges } from '../utils/badgeUtils';

const GOAL_TYPES = {
  WEIGHT_LOSS: 'Perte de Poids',
  WEIGHT_GAIN: 'Prise de Masse / Poids',
  PERFORMANCE: 'Performance / Force',
  GENERAL: 'Général'
};

const checkCompletion = (val, target, type) => {
  if (type === GOAL_TYPES.WEIGHT_LOSS) return val <= target && val > 0;
  return val >= target;
};

const Goals = ({ currentProfile }) => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null); // For updating progress
  
  // New features
  const [latestWeight, setLatestWeight] = useState(null);
  const [linkedToWeight, setLinkedToWeight] = useState(false);
  
  // New Goal Form State
  const [title, setTitle] = useState('');
  const [type, setType] = useState(GOAL_TYPES.WEIGHT_LOSS);
  const [targetValue, setTargetValue] = useState('');
  const [currentValue, setCurrentValue] = useState('0');
  const [unit, setUnit] = useState('kg');
  const [deadline, setDeadline] = useState('');

  // Update Progress Form State
  const [progressValue, setProgressValue] = useState('');

  const loadGoals = async () => {
    if (goals.length === 0) {
      setLoading(true);
    }
    const data = await db.getGoals(currentProfile.id);
    setGoals(data);
    
    // Fetch latest weight for the checkbox feature
    const weights = await db.getWeights(currentProfile.id);
    if (weights && weights.length > 0) {
      weights.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setLatestWeight(weights[0].weight);
    }
    
    setLoading(false);
  };

  useEffect(() => {
    if (currentProfile?.id) {
      loadGoals();
    }
  }, [currentProfile?.id]);

  const handleAddGoal = async (e) => {
    e.preventDefault();
    if (!title || !targetValue) return;

    const newGoal = {
      profileId: currentProfile.id,
      title,
      type,
      targetValue: parseFloat(targetValue),
      currentValue: parseFloat(currentValue) || 0,
      startValue: parseFloat(currentValue) || 0,
      unit,
      deadline,
      linkedToWeight: (type === GOAL_TYPES.WEIGHT_LOSS || type === GOAL_TYPES.WEIGHT_GAIN) && unit.toLowerCase() === 'kg' ? linkedToWeight : false,
      completed: false
    };
    
    newGoal.completed = checkCompletion(newGoal.currentValue, newGoal.targetValue, newGoal.type);

    let goalRewarded = false;
    const today = new Date().toISOString().split('T')[0];

    if (newGoal.completed) {
      const goalsRewardedToday = goals.filter(g => g.rewardedDate === today).length;
      if (goalsRewardedToday < 1) {
        newGoal.rewardedDate = today;
        goalRewarded = true;
      }
    }

    const workouts = await db.getWorkouts(currentProfile.id);
    const badgesBefore = getEarnedBadges(workouts, goals, currentProfile);

    await db.addGoal(newGoal);
    
    if (goalRewarded) {
      await db.addXP(currentProfile.id, 50);
      await db.addCurrency(currentProfile.id, 30, 0);
      setTimeout(() => toast.success("Objectif accompli ! +50 XP, +30 Pièces"), 500);
    } else if (newGoal.completed) {
      setTimeout(() => toast.info("Objectif accompli ! (Limite de 1 objectif rémunéré par jour atteinte)"), 500);
    }
    
    const updatedGoals = [...goals, newGoal];
    const badgesAfter = getEarnedBadges(workouts, updatedGoals, currentProfile);
    const alreadyAwardedIds = new Set([
      ...badgesBefore.map(b => b.id),
      ...workouts.flatMap(w => (w.unlockedBadges || []).map(b => (typeof b === 'object' ? b.id : b)).filter(Boolean)),
      ...(Array.isArray(currentProfile?.badges) ? currentProfile.badges : [])
    ]);
    const newBadges = badgesAfter.filter(b => !alreadyAwardedIds.has(b.id));
    if (newBadges.length > 0) {
      const xpToAward = newBadges.length * 100;
      const coinsToAward = newBadges.length * 50;
      await db.addBadgesToProfile(currentProfile.id, newBadges.map(b => b.id));
      await db.addXP(currentProfile.id, xpToAward);
      await db.addCurrency(currentProfile.id, coinsToAward, 0);
      setTimeout(() => toast.success(`Succès débloqué ! +${xpToAward} XP, +${coinsToAward} Pièces`), 1000);
    }
    
    setShowAddModal(false);
    resetForm();
    loadGoals();
  };

  const resetForm = () => {
    setTitle('');
    setType(GOAL_TYPES.WEIGHT_LOSS);
    setTargetValue('');
    setCurrentValue('0');
    setUnit('kg');
    setDeadline('');
    setLinkedToWeight(false);
  };

  const handleUpdateProgress = async (e) => {
    e.preventDefault();
    if (!selectedGoal || progressValue === '') return;

    const val = parseFloat(progressValue);
    const isCompleted = checkCompletion(val, selectedGoal.targetValue, selectedGoal.type);
    const wasCompleted = selectedGoal.completed;
    
    let goalRewarded = false;
    const today = new Date().toISOString().split('T')[0];
    let newRewardedDate = selectedGoal.rewardedDate;

    if (isCompleted && !wasCompleted) {
      const goalsRewardedToday = goals.filter(g => g.rewardedDate === today).length;
      if (goalsRewardedToday < 1) {
        newRewardedDate = today;
        goalRewarded = true;
      }
    }

    const updated = {
      ...selectedGoal,
      currentValue: val,
      completed: isCompleted,
      rewardedDate: newRewardedDate === undefined ? null : newRewardedDate
    };

    const workouts = await db.getWorkouts(currentProfile.id);
    const badgesBefore = getEarnedBadges(workouts, goals);

    await db.updateGoal(updated);
    
    if (goalRewarded) {
      await db.addXP(currentProfile.id, 50);
      await db.addCurrency(currentProfile.id, 30, 0);
      setTimeout(() => toast.success("Objectif accompli ! +50 XP, +30 Pièces"), 500);
    } else if (isCompleted && !wasCompleted) {
      setTimeout(() => toast.info("Objectif accompli ! (Limite de 1 objectif rémunéré par jour atteinte)"), 500);
    }
    
    // Bidirectional sync: if it's a linked weight goal, update the Body Tracker too
    if (selectedGoal.linkedToWeight) {
      await db.addWeight({
        profileId: currentProfile.id,
        date: new Date().toISOString().split('T')[0],
        weight: val
      });
    }

    const updatedGoals = goals.map(g => g.id === updated.id ? updated : g);
    const badgesAfter = getEarnedBadges(workouts, updatedGoals, currentProfile);
    const alreadyAwardedIds = new Set([
      ...badgesBefore.map(b => b.id),
      ...workouts.flatMap(w => (w.unlockedBadges || []).map(b => (typeof b === 'object' ? b.id : b)).filter(Boolean)),
      ...(Array.isArray(currentProfile?.badges) ? currentProfile.badges : [])
    ]);
    const newBadges = badgesAfter.filter(b => !alreadyAwardedIds.has(b.id));
    if (newBadges.length > 0) {
      const xpToAward = newBadges.length * 100;
      const coinsToAward = newBadges.length * 50;
      await db.addBadgesToProfile(currentProfile.id, newBadges.map(b => b.id));
      await db.addXP(currentProfile.id, xpToAward);
      await db.addCurrency(currentProfile.id, coinsToAward, 0);
      setTimeout(() => toast.success(`Succès débloqué ! +${xpToAward} XP, +${coinsToAward} Pièces`), 1000);
    }
    
    setSelectedGoal(null);
    setProgressValue('');
    loadGoals();
  };

  const handleDelete = async (id) => {
    confirmDialog("Supprimer cet objectif ?", async () => {
      await db.deleteGoal(id);
      loadGoals();
      toast.success("Objectif supprimé avec succès.");
    });
  };

  const getPercentage = (current, target, type, start = null) => {
    if (start === null || start === undefined) {
      // Legacy behavior
      if (target === 0) return 0;
      let p = type === GOAL_TYPES.WEIGHT_LOSS ? (target / current) * 100 : (current / target) * 100;
      return Math.min(Math.max(p, 0), 100).toFixed(0);
    }
    const totalDistance = Math.abs(target - start);
    if (totalDistance === 0) return 100;
    const distanceCovered = type === GOAL_TYPES.WEIGHT_LOSS ? start - current : current - start;
    let p = (distanceCovered / totalDistance) * 100;
    return Math.min(Math.max(p, 0), 100).toFixed(0);
  };

  const activeGoals = goals.filter(g => !g.completed);
  const completedGoals = goals.filter(g => g.completed);

  return (
    <div className="goals-page">
      <div className="flex justify-between items-center mb-4">
        <h2>Objectifs & Succès</h2>
        <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
          <Plus size={20} /> Nouvel Objectif
        </button>
      </div>

      <div className="goals-columns">
        
        {/* ACTIVE GOALS */}
        <div className="goals-column">
          <h3 className="mb-3 flex items-center gap-2" style={{ color: 'var(--primary-color)' }}>
            <Target size={24} /> En Cours
          </h3>
          
          {loading ? (
            <p>Chargement...</p>
          ) : activeGoals.length === 0 ? (
            <div className="card text-center" style={{ padding: '3rem 1rem', opacity: 0.7, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Target size={48} style={{ margin: '0 auto', marginBottom: '1rem', color: 'var(--text-secondary)' }} />
              <p>Aucun objectif en cours.</p>
              <p style={{ fontSize: '0.9rem' }}>Fixe-toi un but pour rester motivé !</p>
            </div>
          ) : (
            <div className="flex-col gap-2">
              {activeGoals.map(goal => {
                const percent = getPercentage(goal.currentValue, goal.targetValue, goal.type, goal.startValue);
                return (
                  <div key={goal.id} className="card" style={{ position: 'relative' }}>
                    <button 
                      onClick={() => handleDelete(goal.id)}
                      style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'transparent', border: 'none', color: 'var(--danger-color)', cursor: 'pointer' }}
                    >
                      <Trash2 size={16} />
                    </button>
                    
                    <h4 className="mb-1" style={{ paddingRight: '2rem' }}>{goal.title}</h4>
                    <div className="flex justify-between items-center mb-2" style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      <span className="flex items-center gap-1"><TrendingUp size={14} /> {goal.type}</span>
                      {goal.deadline && <span className="flex items-center gap-1"><Calendar size={14} /> {new Date(goal.deadline).toLocaleDateString()}</span>}
                    </div>
                    
                    <div className="mb-2">
                      <div className="flex justify-between items-center mb-1">
                        <span style={{ fontWeight: 'bold' }}>{goal.currentValue} {goal.unit}</span>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Cible : {goal.targetValue} {goal.unit}</span>
                      </div>
                      <div style={{ width: '100%', height: '12px', backgroundColor: 'var(--bg-color)', borderRadius: '6px', overflow: 'hidden' }}>
                        <div style={{ width: `${percent}%`, height: '100%', backgroundColor: 'var(--primary-color)', transition: 'width 0.5s ease' }}></div>
                      </div>
                    </div>
                    
                    <button 
                      className="btn btn-outline" 
                      style={{ width: '100%', marginTop: '0.5rem', padding: '0.5rem' }}
                      onClick={() => {
                        setSelectedGoal(goal);
                        setProgressValue(goal.currentValue.toString());
                      }}
                    >
                      Mettre à jour la progression
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* COMPLETED GOALS */}
        <div className="goals-column">
          <h3 className="mb-3 flex items-center gap-2" style={{ color: 'var(--secondary-color)' }}>
            <Trophy size={24} /> Succès
          </h3>
          
          {completedGoals.length === 0 ? (
            <div className="card text-center" style={{ padding: '3rem 1rem', opacity: 0.7, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Trophy size={48} style={{ margin: '0 auto', marginBottom: '1rem', color: 'var(--text-secondary)' }} />
              <p>Tes victoires apparaîtront ici.</p>
            </div>
          ) : (
            <div className="flex-col gap-2">
              {completedGoals.map(goal => (
                <div key={goal.id} className="card" style={{ borderLeft: '4px solid var(--secondary-color)', backgroundColor: 'rgba(163, 217, 177, 0.05)', position: 'relative' }}>
                  <button 
                      onClick={() => handleDelete(goal.id)}
                      style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'transparent', border: 'none', color: 'var(--danger-color)', cursor: 'pointer' }}
                    >
                      <Trash2 size={16} />
                  </button>
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle size={20} color="var(--secondary-color)" />
                    <h4 style={{ margin: 0, paddingRight: '2rem', textDecoration: 'line-through', opacity: 0.8 }}>{goal.title}</h4>
                  </div>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginLeft: '1.75rem' }}>
                    Atteint : {goal.targetValue} {goal.unit}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ADD GOAL MODAL */}
      {showAddModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(5px)' }}>
          <div className="card" style={{ width: '90%', maxWidth: '400px' }}>
            <div className="flex justify-between items-center mb-4">
              <h3>Nouvel Objectif</h3>
              <button className="btn-icon" onClick={() => setShowAddModal(false)}><X size={20} /></button>
            </div>
            
            <form onSubmit={handleAddGoal}>
              <div className="form-group">
                <label className="form-label">Titre</label>
                <input type="text" className="form-control" value={title} onChange={e => setTitle(e.target.value)} placeholder="ex: Soulever 100kg au Squat" required />
              </div>
              
              <div className="form-group">
                <label className="form-label">Type</label>
                <select className="form-control" value={type} onChange={e => setType(e.target.value)}>
                  {Object.values(GOAL_TYPES).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              
              {((type === GOAL_TYPES.WEIGHT_LOSS || type === GOAL_TYPES.WEIGHT_GAIN) && unit.toLowerCase() === 'kg') && (
                <div 
                  className="form-group" 
                  style={{ 
                    marginBottom: '1.5rem', 
                    padding: '1rem', 
                    backgroundColor: 'var(--bg-card)', 
                    borderRadius: 'var(--border-radius)', 
                    border: `1px solid ${linkedToWeight ? 'var(--primary-color)' : 'var(--border-color)'}`,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: linkedToWeight ? '0 0 0 1px var(--primary-color)' : 'none'
                  }}
                  onClick={() => {
                    const newVal = !linkedToWeight;
                    setLinkedToWeight(newVal);
                    if (newVal && latestWeight) {
                      setCurrentValue(latestWeight.toString());
                    }
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', paddingRight: '1rem' }}>
                    <span style={{ fontWeight: '600', color: linkedToWeight ? 'var(--primary-color)' : 'var(--text-color)', transition: 'color 0.3s ease' }}>
                      Lier à l'évolution physique
                    </span>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'flex-start', gap: '0.4rem', lineHeight: '1.4' }}>
                      <Info size={14} style={{ marginTop: '2px', flexShrink: 0, color: linkedToWeight ? 'var(--primary-color)' : 'var(--text-secondary)' }} />
                      {linkedToWeight 
                        ? "Cible un poids précis (ex: Atteindre 75kg). L'objectif se mettra à jour tout seul avec tes pesées !"
                        : "Cible une perte précise (ex: Perdre 10kg). L'objectif sera manuel et non affecté par tes pesées."}
                    </span>
                  </div>
                  
                  {/* Toggle Switch */}
                  <div style={{ 
                    width: '44px', 
                    height: '24px', 
                    backgroundColor: linkedToWeight ? 'var(--primary-color)' : 'var(--bg-color)', 
                    borderRadius: '12px', 
                    position: 'relative', 
                    transition: 'background-color 0.3s ease',
                    flexShrink: 0
                  }}>
                    <div style={{ 
                      width: '18px', 
                      height: '18px', 
                      backgroundColor: '#fff', 
                      borderRadius: '50%', 
                      position: 'absolute', 
                      top: '3px', 
                      left: linkedToWeight ? '23px' : '3px', 
                      transition: 'left 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)', 
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)' 
                    }} />
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 mb-3">
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Départ</label>
                  <input type="number" step="0.1" className="form-control" value={currentValue} onChange={e => setCurrentValue(e.target.value)} disabled={linkedToWeight} required />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Cible</label>
                  <input type="number" step="0.1" className="form-control" value={targetValue} onChange={e => setTargetValue(e.target.value)} required />
                </div>
                <div className="form-group" style={{ width: '80px' }}>
                  <label className="form-label">Unité</label>
                  <input type="text" className="form-control" value={unit} onChange={e => setUnit(e.target.value)} placeholder="kg" />
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Date Limite (Optionnel)</label>
                <input type="date" className="form-control" value={deadline} onChange={e => setDeadline(e.target.value)} />
              </div>
              
              <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>Créer l'Objectif</button>
            </form>
          </div>
        </div>
      )}

      {/* UPDATE PROGRESS MODAL */}
      {selectedGoal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(5px)' }}>
          <div className="card" style={{ width: '90%', maxWidth: '350px', textAlign: 'center' }}>
            <h3 className="mb-2">Progression</h3>
            <p className="mb-4 text-secondary">{selectedGoal.title}</p>
            
            <form onSubmit={handleUpdateProgress}>
              <div className="form-group mb-4">
                <label className="form-label">Où en es-tu ? ({selectedGoal.unit})</label>
                <input 
                  type="number" 
                  step="0.1" 
                  className="form-control" 
                  style={{ textAlign: 'center', fontSize: '1.5rem', padding: '1rem' }}
                  value={progressValue} 
                  onChange={e => setProgressValue(e.target.value)} 
                  required 
                  autoFocus
                />
              </div>
              
              <div className="flex gap-2">
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => setSelectedGoal(null)}>Annuler</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>Valider</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Goals;
